// ADR-103 + AM-XXX — Types mirror Rule DPD-02 / GES-02's nested contract.
// Keep in sync with backend/apps/printing/formats/serializers.py.

export type ElementType =
  | "text"
  | "data_field"
  | "table"
  | "image"
  | "row_number"
  | "group"
  | "shape"
  | "barcode"
  | "qr_code";

export type BorderStyle = "none" | "solid" | "dashed" | "dotted" | "double";

export type FontWeight = "normal" | "bold";
export type TextAlign = "right" | "center" | "left";

export interface BaseCanvasElement {
  id: string;
  type: ElementType;
  x: number;
  y: number;
  w: number;
  h: number;
  border_style?: BorderStyle;
  border_width?: number;
  border_color?: string;
  background_color?: string;
  font_size?: number;
  font_weight?: FontWeight;
  font_family?: string;
  text_align?: TextAlign;
  group_id?: string;
  depends_on_field_key?: string;
  evaluator_type?: "raw" | "boolean_check" | "equals_check" | "not_equals_check";
  target_value?: string;
}

export interface TextElement extends BaseCanvasElement {
  type: "text";
  content: string;
}

export interface DataFieldElement extends BaseCanvasElement {
  type: "data_field";
  field_key: string;
}

export interface TableElement extends BaseCanvasElement {
  type: "table";
  field_key: string;
  columns: string[];
}

export interface ImageElement extends BaseCanvasElement {
  type: "image";
  src: string;
}

export interface RowNumberElement extends BaseCanvasElement {
  type: "row_number";
  start_from?: number;
}

export interface GroupElement extends BaseCanvasElement {
  type: "group";
  /** Optional custom Persian display name for the group (e.g. "قطعات وارداتی") */
  name?: string;
  /** Related data array key (e.g. "items") */
  repeat_source: string;
  /** Row template height in grid units */
  row_height: number;
  /** Child template elements inside group */
  children: CanvasElement[];
}

export interface ShapeElement extends BaseCanvasElement {
  type: "shape";
  shape_kind: "line_h" | "line_v" | "rect";
  stroke_width?: number;
  stroke_color?: string;
  stroke_style?: BorderStyle;
  fill_color?: string;
}

export interface BarcodeElement extends BaseCanvasElement {
  type: "barcode";
  field_key?: string;
  content?: string;
  show_text?: boolean;
}

export interface QrCodeElement extends BaseCanvasElement {
  type: "qr_code";
  field_key?: string;
  content?: string;
}

export type CanvasElement =
  | TextElement
  | DataFieldElement
  | TableElement
  | ImageElement
  | RowNumberElement
  | GroupElement
  | ShapeElement
  | BarcodeElement
  | QrCodeElement;

export type PageSize = "A4" | "A5" | "LABEL_100x150";

export type RevisionStatus = "DRAFT" | "PUBLISHED" | "SUPERSEDED";

export interface PrintFormatRevisionSummary {
  id: string;
  revision_no: number;
  status: RevisionStatus;
  is_default: boolean;
  published_at: string | null;
}

export interface PrintFormatFamily {
  id: string;
  code: string;
  name: string;
  report_type: string;
  is_active: boolean;
  latest_published_revision: PrintFormatRevisionSummary | null;
}

export interface PrintFormatRevision {
  id: string;
  family: string;
  revision_no: number;
  status: RevisionStatus;
  schema_json: CanvasElement[];
  page_size: PageSize;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

// --- AM-XXX: nested field/table metadata, Persian labels ------------------

/** A single printable field, as exposed by a domain's Serializer (Rule GES-02-R1). */
export interface PrintableFieldOption {
  key: string;
  label: string;
}

/** A printable related-table relation (e.g. BOM items), with its own columns. */
export interface PrintableTableOption {
  key: string;
  label: string;
  columns: PrintableFieldOption[];
}

export interface ReportTypeOption {
  key: string;
  /** Persian display name, e.g. "نسخهٔ BOM" — never the raw report_type key. */
  label: string;
  /**
   * May be empty while the owning domain hasn't migrated to AM-XXX yet
   * (Rule GES-04-R1) — render an explicit "not yet defined" state, never a
   * silently empty dropdown that looks broken.
   */
  available_fields: PrintableFieldOption[];
  available_tables: PrintableTableOption[];
}

export const PX_PER_MM = 3.7795275591; // 96dpi / 25.4mm

export const PAGE_DIMENSIONS_MM: Record<PageSize, { widthMm: number; heightMm: number }> = {
  A4: { widthMm: 210, heightMm: 297 },
  A5: { widthMm: 148, heightMm: 210 },
  LABEL_100x150: { widthMm: 100, heightMm: 150 },
};
